import {
    Add,
    Cached,
    Edit,
    PersonRemove
} from '@mui/icons-material'
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardHeader,
    Chip,
    FormControlLabel,
    IconButton,
    Stack,
    Switch,
    TextField,
    Tooltip
} from '@mui/material'
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { WrapperTable } from '../components'
import { HttpWFInicioFirma } from './api'
import DialogWFInicioFirma from './DialogWFInicioFirma'
import { Opcion, WFInicioFirma, WFInicioFirmaCatalogos } from './types'

const catalogosVacios: WFInicioFirmaCatalogos = { etapas: [], grupos: [], resultados: [] }

const WFInicioFirmaAdmin = () => {
    const { askConfirmation } = useConfirmationDialog()
    const [paises, setPaises] = useState<Opcion[]>([])
    const [sistemas, setSistemas] = useState<Opcion[]>([])
    const [pais, setPais] = useState<Opcion | null>(null)
    const [sistema, setSistema] = useState<Opcion | null>(null)
    const [soloActivos, setSoloActivos] = useState(true)
    const [rows, setRows] = useState<WFInicioFirma[]>([])
    const [catalogos, setCatalogos] = useState<WFInicioFirmaCatalogos>(catalogosVacios)
    const [loading, setLoading] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [relacionEditada, setRelacionEditada] = useState<WFInicioFirma | null>(null)

    useEffect(() => {
        HttpCacheWorkflow.getSistemasPrincipales().then(data =>
            setPaises(data.map(item => ({ id: item.id, descripcion: item.descripcion })))
        )
    }, [])

    const cambiarPais = (_: unknown, value: Opcion | null) => {
        setPais(value)
        setSistema(null)
        setSistemas([])
        setRows([])
        setCatalogos(catalogosVacios)

        if (value) {
            HttpCacheWorkflow.getSistemasPorSistemaPrincipal(value.id).then(data =>
                setSistemas(data.map(item => ({ id: item.id, descripcion: item.descripcion })))
            )
        }
    }

    const cargar = useCallback(() => {
        if (!sistema) {
            setRows([])
            
return Promise.resolve()
        }

        setLoading(true)
        
return Promise.all([
            HttpWFInicioFirma.getBySistema(sistema.id, soloActivos),
            HttpWFInicioFirma.getCatalogos(sistema.id)
        ]).then(([relaciones, opciones]) => {
            setRows(relaciones)
            setCatalogos(opciones)
        }).finally(() => setLoading(false))
    }, [sistema, soloActivos])

    useEffect(() => {
        cargar()
    }, [cargar])

    const abrirAlta = () => {
        setRelacionEditada(null)
        setDialogOpen(true)
    }

    const abrirEdicion = (relacion: WFInicioFirma) => {
        setRelacionEditada(relacion)
        setDialogOpen(true)
    }

    const inactivar = (relacion: WFInicioFirma) => askConfirmation(() => {
        toast.promise(HttpWFInicioFirma.deactivate(relacion.intIdWFInicioFirma), {
            loading: 'Inactivando...',
            success: 'Relación inactivada',
            error: 'No se pudo inactivar la relación'
        }).then(cargar)
    }, `¿Inactivar la configuración de la etapa ${relacion.vchNombreEtapa}?`)

    const columns = useMemo<GridColDef[]>(() => [
        { field: 'tinOrden', headerName: 'Orden', width: 85 },
        {
            field: 'vchNombreEtapa',
            headerName: 'Etapa',
            flex: 2,
            minWidth: 220,
            valueGetter: ({ row }) => `${row.vchNombreEtapa} (${row.intIdEtapa})`
        },
        {
            field: 'vchNombreGrupo',
            headerName: 'Grupo',
            flex: 1.5,
            minWidth: 180,
            valueGetter: ({ row }) => row.vchNombreGrupo || 'Todos / Sin grupo'
        },
        {
            field: 'bitRequiereResultado',
            headerName: 'Requiere resultado',
            width: 155,
            renderCell: ({ value }) => <Chip size='small' label={value ? 'Sí' : 'No'} color={value ? 'info' : 'default'} />
        },
        {
            field: 'vchFirmaResultadoTipoDescripcion',
            headerName: 'Tipo de resultado',
            flex: 1.5,
            minWidth: 180
        },
        {
            field: 'bitActivo',
            headerName: 'Estado',
            width: 105,
            renderCell: ({ value }) => <Chip size='small' label={value ? 'Activo' : 'Inactivo'} color={value ? 'success' : 'default'} />
        },
        {
            field: 'actions',
            type: 'actions',
            width: 70,
            getActions: ({ row }) => [
                <GridActionsCellItem
                    key='edit'
                    icon={<Tooltip title='Editar'><Edit color='info' /></Tooltip>}
                    label='Editar'
                    onClick={() => abrirEdicion(row)}
                    showInMenu
                />,
                ...(row.bitActivo ? [
                    <GridActionsCellItem
                        key='deactivate'
                        icon={<Tooltip title='Inactivar'><PersonRemove color='error' /></Tooltip>}
                        label='Inactivar'
                        onClick={() => inactivar(row)}
                        showInMenu
                    />
                ] : [])
            ]
        }
    ], [cargar])

    const ordenSugerido = Math.min(255, Math.max(0, ...rows.map(row => row.tinOrden ?? 0)) + 1)
    const rowsOrdenadas = [...rows].sort((a, b) =>
        a.tinOrdenEtapa - b.tinOrdenEtapa ||
        (a.tinOrden ?? 255) - (b.tinOrden ?? 255)
    )

    return (
        <WrapperTable>
            <Card>
                <CardHeader
                    title='Configuración de Inicio de Firmas'
                    action={
                        <Stack direction='row' spacing={1}>
                            <Tooltip title='Actualizar'>
                                <span>
                                    <IconButton onClick={cargar} disabled={!sistema || loading}><Cached /></IconButton>
                                </span>
                            </Tooltip>
                            <Button variant='contained' startIcon={<Add />} onClick={abrirAlta} disabled={!sistema}>
                                Agregar relación
                            </Button>
                        </Stack>
                    }
                />

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mx: 2, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Autocomplete
                        options={paises}
                        value={pais}
                        onChange={cambiarPais}
                        getOptionLabel={option => option.descripcion}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        sx={{ minWidth: 220, flex: 1 }}
                        size='small'
                        renderInput={params => <TextField {...params} label='País *' />}
                    />
                    <Autocomplete
                        options={sistemas}
                        value={sistema}
                        onChange={(_, value) => setSistema(value)}
                        getOptionLabel={option => option.descripcion}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        disabled={!pais}
                        sx={{ minWidth: 260, flex: 1 }}
                        size='small'
                        renderInput={params => <TextField {...params} label='Sistema *' />}
                    />
                    <FormControlLabel
                        control={<Switch checked={!soloActivos} onChange={event => setSoloActivos(!event.target.checked)} />}
                        label='Ver todos'
                    />
                </Box>

                <Box sx={{ mx: 2, mb: 2 }}>
                    <DataGrid
                        rows={rowsOrdenadas}
                        columns={columns}
                        getRowId={row => row.intIdWFInicioFirma}
                        loading={loading}
                        disableColumnMenu
                        density='compact'
                        autoHeight
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                    />
                </Box>
            </Card>

            {sistema && (
                <DialogWFInicioFirma
                    open={dialogOpen}
                    sinCodSistema={sistema.id}
                    catalogos={catalogos}
                    relacion={relacionEditada}
                    ordenSugerido={ordenSugerido}
                    onClose={() => setDialogOpen(false)}
                    onSaved={cargar}
                />
            )}
        </WrapperTable>
    )
}

export default WFInicioFirmaAdmin
